// ABOUTME: Detail page for managing a CSV datasource (view files, add files, fix errors).
// ABOUTME: Shows file list with status badges, sample data preview, and file management actions.

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Loader2,
  Trash2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { getToken, getWorkspaceId, API_BASE_URL } from '@/services/api/client';
import { ColumnEditor, type InferredColumn, type ColumnMapping } from '@/components/data-source/ColumnEditor';

interface CsvFile {
  id: string;
  original_filename: string;
  table_name: string;
  display_name: string;
  file_size_bytes: number;
  file_type: string;
  status: 'processing' | 'ready' | 'failed';
  error_message: string | null;
  error_category?: string | null;
  fixable?: boolean;
  uploaded_at: string;
}

interface CsvDatasource {
  id: string;
  name: string;
  csv_schema_name: string;
  status: string;
  files: CsvFile[];
  created_at: string;
}

interface PreviewData {
  columns: { name: string; type: string }[];
  rows: string[][];
}

const MAX_FILES_PER_DATASOURCE = 20;

export function CsvDatasourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [datasource, setDatasource] = useState<CsvDatasource | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedFile, setExpandedFile] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<Record<string, PreviewData>>({});
  const [loadingPreview, setLoadingPreview] = useState<string | null>(null);
  const [fixingFileId, setFixingFileId] = useState<string | null>(null);
  const [schemaColumns, setSchemaColumns] = useState<InferredColumn[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDatasource = useCallback(async () => {
    if (!id) return;
    try {
      const token = await getToken?.();
      const workspaceId = getWorkspaceId?.();
      const resp = await fetch(`${API_BASE_URL}/data-sources/${id}/csv`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
        },
      });
      if (resp.ok) {
        setDatasource(await resp.json());
      } else {
        setError('Failed to load datasource');
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDatasource();
  }, [fetchDatasource]);

  const loadPreview = useCallback(async (fileId: string) => {
    if (!id || previewData[fileId]) return;
    setLoadingPreview(fileId);
    try {
      const token = await getToken?.();
      const workspaceId = getWorkspaceId?.();
      const resp = await fetch(`${API_BASE_URL}/data-sources/${id}/csv/files/${fileId}/preview`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
        },
      });
      if (resp.ok) {
        const data = await resp.json();
        setPreviewData((prev) => ({ ...prev, [fileId]: data }));
      }
    } catch {
      // Preview load failure is non-critical
    } finally {
      setLoadingPreview(null);
    }
  }, [id, previewData]);

  const toggleExpand = (fileId: string, status: string) => {
    if (expandedFile === fileId) {
      setExpandedFile(null);
    } else {
      setExpandedFile(fileId);
      if (status === 'ready') loadPreview(fileId);
    }
  };

  const handleFixFile = async (fileId: string) => {
    if (!id) return;
    setIsLoadingSchema(true);
    setFixingFileId(fileId);
    try {
      const token = await getToken?.();
      const workspaceId = getWorkspaceId?.();
      const resp = await fetch(`${API_BASE_URL}/data-sources/${id}/csv/files/${fileId}/schema`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
        },
      });
      if (resp.ok) {
        const data = await resp.json();
        setSchemaColumns(data.columns.map((col: { name: string; type: string }) => ({
          name: col.name,
          type: col.type,
          hasError: true,
        })));
      }
    } catch {
      setError('Failed to load schema');
    } finally {
      setIsLoadingSchema(false);
    }
  };

  const handleRetryWithSchema = async (mappings: ColumnMapping[]) => {
    if (!id || !fixingFileId) return;
    setIsRetrying(true);
    try {
      const token = await getToken?.();
      const workspaceId = getWorkspaceId?.();
      const resp = await fetch(
        `${API_BASE_URL}/data-sources/${id}/csv/files/${fixingFileId}/retry-with-schema`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
          },
          body: JSON.stringify({
            columns: mappings.map((m) => ({
              original_name: m.originalName,
              original_type: m.originalType,
              new_name: m.newName,
              new_type: m.newType,
            })),
          }),
        }
      );
      if (resp.ok) {
        setFixingFileId(null);
        setSchemaColumns([]);
        await fetchDatasource();
      } else {
        const err = await resp.json().catch(() => ({ detail: 'Retry failed' }));
        setError(err.detail || 'Retry failed');
      }
    } catch {
      setError('Network error during retry');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!id) return;
    try {
      const token = await getToken?.();
      const workspaceId = getWorkspaceId?.();
      await fetch(`${API_BASE_URL}/data-sources/${id}/csv/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
        },
      });
      setDeleteConfirmId(null);
      await fetchDatasource();
    } catch {
      setError('Failed to delete file');
    }
  };

  const handleAddFiles = async (fileList: FileList) => {
    if (!id) return;
    const token = await getToken?.();
    const workspaceId = getWorkspaceId?.();
    const formData = new FormData();
    Array.from(fileList).forEach((f) => formData.append('files', f));

    try {
      const resp = await fetch(`${API_BASE_URL}/data-sources/${id}/csv/files`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(workspaceId ? { 'X-Workspace-ID': workspaceId } : {}),
        },
        body: formData,
      });
      if (resp.ok) {
        await fetchDatasource();
      } else {
        const err = await resp.json().catch(() => ({ detail: 'Upload failed' }));
        setError(err.detail || 'Upload failed');
      }
    } catch {
      setError('Network error during upload');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (error && !datasource) {
    return (
      <div className="max-w-2xl mx-auto py-8 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <p className="text-red-600">{error}</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  if (!datasource) return null;

  const fileCount = datasource.files.length;
  const readyCount = datasource.files.filter((f) => f.status === 'ready').length;
  const failedCount = datasource.files.filter((f) => f.status === 'failed').length;
  const canAddFiles = fileCount < MAX_FILES_PER_DATASOURCE;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-surface-900">{datasource.name}</h1>
          <p className="text-sm text-surface-500">
            {fileCount} file{fileCount !== 1 ? 's' : ''} - {readyCount} ready
            {failedCount > 0 && `, ${failedCount} failed`}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-md">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">dismiss</button>
        </div>
      )}

      {/* Fix UI (column editor) */}
      {fixingFileId && schemaColumns.length > 0 && (
        <div className="mb-6 p-4 border border-surface-200 rounded-md bg-surface-50">
          <h3 className="text-sm font-medium text-surface-700 mb-3">
            Fix columns for: {datasource.files.find((f) => f.id === fixingFileId)?.table_name}
          </h3>
          <ColumnEditor
            columns={schemaColumns}
            onRetry={handleRetryWithSchema}
            onCancel={() => { setFixingFileId(null); setSchemaColumns([]); }}
            isRetrying={isRetrying}
          />
        </div>
      )}

      {/* File list */}
      <div className="border border-surface-200 rounded-md overflow-x-auto mb-6">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="border-b border-surface-200 bg-surface-50">
              <th className="px-4 py-2 text-left text-surface-600 font-medium w-8" />
              <th className="px-4 py-2 text-left text-surface-600 font-medium">File</th>
              <th className="px-4 py-2 text-left text-surface-600 font-medium">Table Name</th>
              <th className="px-4 py-2 text-left text-surface-600 font-medium">Status</th>
              <th className="px-4 py-2 text-left text-surface-600 font-medium">Size</th>
              <th className="px-4 py-2 text-left text-surface-600 font-medium w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {datasource.files.map((file) => (
              <>
                <tr
                  key={file.id}
                  className="border-b border-surface-100 hover:bg-surface-50 cursor-pointer"
                  onClick={() => toggleExpand(file.id, file.status)}
                >
                  <td className="px-4 py-2">
                    {expandedFile === file.id ? (
                      <ChevronDown className="w-4 h-4 text-surface-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-surface-400" />
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-surface-400" />
                      <span className="text-surface-900">{file.original_filename}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-surface-700 font-mono text-xs">{file.table_name}</td>
                  <td className="px-4 py-2">
                    {file.status === 'ready' && (
                      <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full text-xs">
                        <CheckCircle className="w-3 h-3" /> Ready
                      </span>
                    )}
                    {file.status === 'processing' && (
                      <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full text-xs">
                        <Loader2 className="w-3 h-3 animate-spin" /> Processing
                      </span>
                    )}
                    {file.status === 'failed' && (
                      <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-2 py-0.5 rounded-full text-xs">
                        <AlertCircle className="w-3 h-3" /> Failed
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-surface-500 text-xs">
                    {(file.file_size_bytes / 1024).toFixed(1)} KB
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {file.status === 'failed' && file.fixable && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFixFile(file.id)}
                          disabled={isLoadingSchema}
                          data-testid={`fix-btn-${file.id}`}
                        >
                          Fix
                        </Button>
                      )}
                      {deleteConfirmId === file.id ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteFile(file.id)}
                            className="text-red-600"
                          >
                            Confirm
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirmId(null)}
                          >
                            No
                          </Button>
                        </div>
                      ) : (
                        datasource.files.length > 1 && (
                          <button
                            onClick={() => setDeleteConfirmId(file.id)}
                            className="p-1 rounded hover:bg-surface-100"
                            aria-label={`Delete ${file.original_filename}`}
                          >
                            <Trash2 className="w-4 h-4 text-surface-400" />
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
                {expandedFile === file.id && (
                  <tr key={`${file.id}-expanded`} className="border-b border-surface-100">
                    <td colSpan={6} className="px-8 py-3 bg-surface-25">
                      {file.status === 'failed' && file.error_message && (
                        <div className="text-sm text-red-600 bg-red-50 p-2 rounded mb-2">
                          {file.error_message}
                        </div>
                      )}
                      {file.status === 'ready' && loadingPreview === file.id && (
                        <div className="flex items-center gap-2 text-sm text-surface-500">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading preview...
                        </div>
                      )}
                      {file.status === 'ready' && previewData[file.id] && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-surface-200">
                                {previewData[file.id].columns.map((col) => (
                                  <th key={col.name} className="px-2 py-1 text-left text-surface-500 font-medium">
                                    {col.name}
                                    <span className="text-surface-400 ml-1">({col.type})</span>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {previewData[file.id].rows.slice(0, 5).map((row, ri) => (
                                <tr key={ri} className="border-b border-surface-100">
                                  {row.map((cell, ci) => (
                                    <td key={ci} className="px-2 py-1 text-surface-700 truncate max-w-[150px]">
                                      {cell ?? ''}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {file.status === 'processing' && (
                        <p className="text-sm text-surface-500">File is being processed...</p>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add files section */}
      {canAddFiles && (
        <div className="border border-dashed border-surface-300 rounded-md p-6 text-center">
          <Upload className="w-6 h-6 mx-auto text-surface-400 mb-2" />
          <p className="text-sm text-surface-600 mb-1">Add more files to this datasource</p>
          <p className="text-xs text-surface-400 mb-3">
            {MAX_FILES_PER_DATASOURCE - fileCount} of {MAX_FILES_PER_DATASOURCE} slots remaining
          </p>
          <Button
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".csv,.tsv,.parquet"
            onChange={(e) => e.target.files && handleAddFiles(e.target.files)}
            className="hidden"
            aria-label="Add CSV files"
          />
        </div>
      )}
    </div>
  );
}
